import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import Button from '~/ui/components/common/Button';
import Modal from '~/ui/components/common/Modal';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';

import { IClient, IClientDetailedV3 } from '~/services/api/client/types';

import validationSchema from './validation';

import styles from './RestoreModal.module.scss';

interface IProps {
  client: IClient | IClientDetailedV3;
  onConfirm: (teamId: string) => void;
  onClose: () => void;
}

type IFormValues = {
  teamId: number;
};

type TeamWithArchived = {
  isArchived: boolean;
  team: { id: number; name: string };
};

const RestoreModal: FC<IProps> = ({ client, onConfirm, onClose }) => {
  const teams: TeamWithArchived[] = 'teams' in client ? client.teams : client.actTeams;
  const clientTeams = teams.filter((t: TeamWithArchived) => t.isArchived);

  const form = useForm<IFormValues>({
    defaultValues: { teamId: clientTeams.length === 1 ? clientTeams[0]?.team?.id : null },
    resolver: yupResolver(validationSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = form;

  const onSubmit = (vals: IFormValues) => {
    onConfirm(String(vals.teamId));
  };

  const teamOptions = useMemo(
    () =>
      teams
        .filter((t: TeamWithArchived) => t.isArchived)
        .map((t: TeamWithArchived) => ({ label: t.team.name, value: t.team.id })),
    [teams],
  );

  return (
    <Modal open onClose={onClose} maxWidth="sm" className={styles.modal}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <p>
          It appears you are restoring {client?.firstName} {client?.lastName}. Previous Discharge
          Date and Reason will be removed.
        </p>
        <div className={styles.input}>
          <SelectComponent
            name="teamId"
            hideSelectedOptions={false}
            options={teamOptions}
            control={control}
            errors={errors}
            maxMenuHeight={200}
            label="Select Team"
          />
        </div>
        <div className={styles.buttonsWrapper}>
          <div className={styles.buttonMargin}>
            <Button color="primary" variant="outlined" onClick={onClose}>
              Cancel
            </Button>
          </div>
          <Button type="submit" color="primary" variant="contained" disabled={!isValid}>
            Proceed
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RestoreModal;
